import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function TasksByUser() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [editors, setEditors] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [operators, setOperators] = useState([]);
  
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState('client');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [message, setMessage] = useState('');
  const [hoveredText, setHoveredText] = useState({ id: null, type: '', text: '' });
  
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);

  const normalizeName = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/\(\s*id\s*[:#-]?\s*\d+\s*\)/gi, '')
      .replace(/^\s*#?\d+\s*[-:]\s*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getUserName = (user) => {
    return (
      user?.name ||
      user?.full_name ||
      user?.username ||
      user?.client_name ||
      user?.editor_name ||
      user?.designer_name ||
      user?.operator_name ||
      ''
    );
  };


  // 🎨 GLOBAL COLORS DEFINITION - ADD SAFE DEFAULTS
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: (currentTheme && currentTheme.primary) || '#6366F1',
    secondary: (currentTheme && currentTheme.secondary) || '#8B5CF6',
    accent: (currentTheme && currentTheme.accent) || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  };

  // 🎨 COMPLETE STYLES DEFINITION - DEFINE ALL STYLES BEFORE USING THEM
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'auto',
      padding: '20px'
    },
    header: {
      padding: '30px 20px 20px',
      background: 'transparent',
    },
    headerContent: {
      maxWidth: '100%',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '20px'
    },
    pageTitle: {
      fontSize: '2.5rem',
      fontWeight: '800',
      color: 'white',
      margin: '0 0 10px 0',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1.1rem',
      margin: '0 0 20px 0',
      fontWeight: '300'
    },
    messageAlert: {
      background: 'rgba(220, 53, 69, 0.1)',
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 20px 20px',
      border: `1px solid ${colors.danger}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)'
    },
    successAlert: {
      background: 'rgba(16, 185, 129, 0.1)',
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 20px 20px',
      border: `1px solid ${colors.success}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)'
    },
    content: {
      padding: '0 20px 20px',
      maxWidth: '100%',
      margin: '0 auto'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      border: `1px solid ${colors.borderDark}`,
      marginBottom: '25px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      padding: '20px 25px',
      borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
      fontSize: '1.4rem',
      fontWeight: '700',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    cardBody: {
      padding: '25px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    formLabel: {
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      marginBottom: '8px',
      display: 'block'
    },
    formInput: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      padding: '12px 18px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      alignItems: 'end'
    },
    submitBtn: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      padding: '14px 24px',
      borderRadius: '10px',
      fontWeight: '700',
      fontSize: '1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 15px ${colors.primary}40`
    },
    refreshBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `2px solid ${colors.borderDark}`,
      padding: '12px 20px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    dropdownSelect: {
      background: colors.backgroundMedium,
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      padding: '12px 18px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      WebkitAppearance: 'none', 
      MozAppearance: 'none',
      appearance: 'none',
      outline: 'none',
      transition: 'all 0.3s ease',
      backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 15px center',
      backgroundSize: '8px 10px'
    },
    userDetailsCard: {
      background: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      border: `1px solid ${colors.borderDark}`,
      marginBottom: '25px',
      backdropFilter: 'blur(10px)',
      padding: '25px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
    },
    userDetailsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px'
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    detailLabel: {
      color: colors.accent,
      fontSize: '0.85rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    detailValue: {
      color: 'white',
      fontSize: '1.1rem',
      fontWeight: '500'
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginTop: '20px'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '12px',
      padding: '15px',
      border: `1px solid ${colors.borderDark}`,
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    statIcon: {
      fontSize: '2rem',
      width: '50px',
      height: '50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '10px'
    },
    statContent: {
      flex: 1
    },
    statLabel: {
      color: colors.textLight,
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    statValue: {
      color: 'white',
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    tableContainer: {
      overflowX: 'auto',
      width: '100%',
      borderRadius: '0 0 16px 16px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '1400px',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    tableHeader: {
      background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.secondary}20 100%)`,
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '12px 8px',
      fontWeight: '700',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      textAlign: 'left'
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      transition: 'all 0.3s ease'
    },
    tableCell: {
      padding: '12px 8px',
      verticalAlign: 'middle',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      color: 'white'
    },
    textCell: {
      cursor: 'pointer',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transition: 'all 0.3s ease',
      color: 'white'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 20px',
      color: 'white',
      textAlign: 'center'
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: '3px solid transparent',
      borderTop: `3px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      background: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '0 0 16px 16px',
      color: 'white'
    },
    statsBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)'
    },
    tooltip: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.95)',
      color: 'white',
      padding: '20px',
      borderRadius: '10px',
      border: `2px solid ${colors.primary}`,
      maxWidth: '500px',
      maxHeight: '300px',
      overflowY: 'auto',
      zIndex: 9999,
      fontSize: '14px',
      lineHeight: '1.5'
    },
    tooltipHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
      borderBottom: `1px solid ${colors.primary}`,
      paddingBottom: '5px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      fontSize: '16px',
      padding: '5px'
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      color: 'white'
    },
    linkButton: {
      background: 'transparent',
      border: `2px solid ${colors.primary}`,
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '0.75rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      color: 'white'
    },
    actionButton: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '0.75rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.3s ease'
    },
    tableText: {
      color: 'white',
      fontSize: '0.8rem'
    }
  };

  // Fetch all tasks
  useEffect(() => {
    fetchTasks();
  }, []);

  // Fetch users when user type changes
  useEffect(() => {
    if (selectedUserType) {
      fetchUsers(selectedUserType);
    }
  }, [selectedUserType]);

  // Filter tasks when user is selected
  useEffect(() => {
    if (selectedUserId && selectedUserType) {
      filterTasksByUser();
    } else {
      setFilteredTasks([]);
      setSelectedUserDetails(null);
    }
  }, [selectedUserId, selectedUserType, tasks]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      console.log('📊 Fetching all tasks from performance API...');
      
      // Use performance_api.php to get ALL tasks (no limit)
      const response = await fetch(`${API_BASE}/performance_api.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'get_performance_data',
          user_role: 'admin', // Use admin role to get all tasks
          user_id: 0,
          filter_type: '',
          filter_name: '',
          filter_name_id: '',
          status_filter: 'all'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📊 Performance API response:', result);
      
      if (result.success && Array.isArray(result.data)) {
        console.log(`✅ Loaded ${result.data.length} tasks from all tables`);
        
        // Map the data to match your existing structure
        const allTasks = result.data.map(task => {
          // Determine source based on source_table
          let source = 'tasks';
          if (task.source_table === 'editor_tasks') {
            source = 'editor-tasks';
          } else if (task.source_table === 'designer_tasks') {
            source = 'designer-tasks';
          }
          
          // Map IDs properly
          let id, original_id;
          if (source === 'editor-tasks') {
            id = `ET-${task.id}`;
            original_id = task.id;
          } else if (source === 'designer-tasks') {
            id = `DT-${task.id}`;
            original_id = task.id;
          } else {
            id = task.id;
            original_id = task.id;
          }
          
          // Map client notes from performance API data
          const client_notes = task.client_notes || task.client_feedback || task.description || '';
          const client_feedback_notes = task.client_notes || task.client_feedback || '';
          
          // Get editor_id, designer_id, client_id from the data if available
          // For editor_tasks, we need to extract IDs from editor_name and designer_name
          let editor_id = task.editor_id || null;
          let designer_id = task.designer_id || null;
          let client_id = task.client_id || null;
          let operator_id = task.operator_id || null;
          let created_by = task.created_by || null;
          let assigned_by = task.assigned_by || null;
          
          // For editor-tasks, we need to find IDs from names
          if (source === 'editor-tasks') {
            // These will be populated when we fetch users
            editor_id = null;
            designer_id = null;
            client_id = null;
          }
          
          return {
            // Basic fields
            id: id,
            original_id: original_id,
            source: source,
            
            // Video details
            video_name: task.video_name || task.task_name || 'Unnamed Task',
            video_type: task.video_type || 'Not Specified',
            video_count: task.video_count || 1,
            
            // People names
            client_name: task.client_name || 'Unknown Client',
            editor_name: task.editor_name || 'Unassigned',
            designer_name: task.designer_name || 'Unassigned',
            operator_name: task.operator_name || 'Unassigned',
            
            // Status fields
            status: task.status || 'pending',
            editor_status: task.editor_status || task.status || 'pending',
            designer_status: task.designer_status || task.status || 'pending',
            client_status: task.client_status || 'pending',
            toggle_status: 'not proved',
            
            // Links
            raw_link: task.raw_link || task.reference_video_link || '',
            editor_link: task.edited_video_link || task.submitted_url || '',
            edited_video_link: task.edited_video_link || task.submitted_url || '',
            thumbnail_link: task.thumbnail_link || task.thumbnail_url || '',
            reference_video_link: task.reference_video_link || '',
            
            // Notes
            notes: task.description || task.notes || '',
            editor_notes: task.editor_notes || '',
            designer_notes: task.designer_notes || '',
            client_feedback_notes: client_feedback_notes,
            client_notes: client_notes,
            
            // Dates
            shoot_date: task.due_date || task.shoot_date || task.created_at || '',
            created_at: task.created_at || '',
            assigned_at: task.assigned_at || '',
            
            // IDs (will be populated when filtering)
            client_id: client_id,
            editor_id: editor_id,
            designer_id: designer_id,
            operator_id: operator_id,
            created_by: created_by,
            assigned_by: assigned_by,
            
            // Additional fields for different task types
            is_approved: task.is_approved || 0,
            designer_completed_at: task.designer_completed_at || null,
            completed_at: task.completed_at || null,
            submitted_at: task.submitted_at || null,
            admin_notes: task.admin_notes || '',
            approved_at: task.approved_at || ''
          };
        });
        
        // Always fetch designer tasks directly to avoid missing details
        const designerTasks = await fetchDesignerTasksDirect();

        const mergedTasksMap = new Map();
        [...allTasks, ...designerTasks].forEach(task => {
          const key = `${task.source}:${task.original_id || task.id}`;
          mergedTasksMap.set(key, task);
        });

        const mergedTasks = Array.from(mergedTasksMap.values());

        console.log('📋 Mapped all tasks:', mergedTasks.length);
        console.log('📊 Tasks breakdown:', {
          tasks: mergedTasks.filter(t => t.source === 'tasks').length,
          editorTasks: mergedTasks.filter(t => t.source === 'editor-tasks').length,
          designerTasks: mergedTasks.filter(t => t.source === 'designer-tasks').length
        });
        
        setTasks(mergedTasks);
        
      } else {
        console.error('Performance API error:', result.error);
        // Fallback to original method
        fetchTasksFallback();
      }
      
    } catch (error) {
      console.error('❌ Error fetching all tasks:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessage('❌ Error loading tasks: ' + errorMsg);
      // Fallback to original method
      fetchTasksFallback();
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignerTasksDirect = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designer_tasks.php`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }

      const data = await response.json();
      let designerTasksArray = [];

      if (Array.isArray(data)) {
        designerTasksArray = data;
      } else if (data && typeof data === 'object') {
        if (data.data && Array.isArray(data.data)) {
          designerTasksArray = data.data;
        } else if (data.success !== false) {
          designerTasksArray = data.tasks || [data];
        }
      }

      return designerTasksArray.map(task => ({
        ...task,
        source: 'designer-tasks',
        id: `DT-${task.id}`,
        original_id: task.id,
        toggle_status: task.toggle_status || 'not proved',
        client_feedback_notes: task.client_feedback_notes || task.client_feedback || '',
        client_status: task.client_status || 'pending',
        editor_status: task.editor_status || 'pending',
        designer_status: task.status || 'pending',
        video_name: task.video_name || task.task_name || task.description || 'Unnamed Task',
        client_name: task.client_name || 'Unknown Client',
        editor_name: task.editor_name || 'Unassigned',
        designer_name: task.designer_name || task.assigned_designer || 'Unassigned',
        operator_name: task.operator_name || 'N/A',
        video_type: task.video_type || 'Design Task',
        editor_link: task.editor_link || task.edited_video_link || task.submitted_url || '',
        edited_video_link: task.edited_video_link || task.submitted_url || '',
        thumbnail_link: task.thumbnail_link || task.thumbnail_url || task.submitted_url || '',
        notes: task.notes || task.description || task.designer_notes || '',
        client_id: task.client_id || null,
        editor_id: task.editor_id || null,
        designer_id: task.designer_id || null,
        operator_id: null,
        assigned_by: task.assigned_by || null,
        status: task.status || 'pending',
        raw_link: task.raw_link || '',
        shoot_date: task.shoot_date || task.created_at || '',
        video_count: task.video_count || 1,
        completed_at: task.completed_at || null,
        submitted_at: task.submitted_at || null
      }));
    } catch (error) {
      console.error('❌ Error fetching designer tasks directly:', error);
      return [];
    }
  };

  // Fallback method using individual APIs
  const fetchTasksFallback = async () => {
    try {
      console.log('🔄 Using fallback method...');
      
      const [tasksResponse, editorTasksResponse, designerTasksResponse] = await Promise.all([
        fetch(`${API_BASE}/get_tasks.php`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => ({ ok: false, json: async () => ({ data: [] }) })),
        
        fetch(`${API_BASE}/get_editor_tasks.php`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => ({ ok: false, json: async () => ({ data: [] }) })),
        
        fetch(`${API_BASE}/get_designer_tasks.php`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => ({ ok: false, json: async () => ({ data: [] }) }))
      ]);

      let allTasks = [];

      // Process regular tasks
      if (tasksResponse.ok) {
        const contentType = tasksResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await tasksResponse.json();
          let tasksArray = [];
          
          if (Array.isArray(data)) {
            tasksArray = data;
          } else if (data && typeof data === 'object') {
            if (data.data && Array.isArray(data.data)) {
              tasksArray = data.data;
            } else if (data.success !== false) {
              tasksArray = [data];
            }
          }
          
          console.log('📊 Main tasks fetched:', tasksArray.length);
          
          const mappedTasks = tasksArray.map(task => ({
            ...task,
            source: 'tasks',
            original_id: task.id,
            toggle_status: task.toggle_status || 'not proved',
            client_feedback_notes: task.client_feedback_notes || task.client_feedback || '',
            client_status: task.client_status || 'pending',
            editor_status: task.editor_status || 'pending',
            designer_status: task.designer_status || 'pending',
            editor_link: task.editor_link || task.edited_video_link || '',
            video_name: task.video_name || 'Unnamed Task',
            client_name: task.client_name || 'Unknown Client',
            operator_name: task.operator_name || 'Unassigned',
            editor_name: task.editor_name || 'Unassigned',
            designer_name: task.designer_name || 'Unassigned',
            video_type: task.video_type || 'Not Specified',
            notes: task.notes || '',
            editor_notes: task.editor_notes || '',
            designer_notes: task.designer_notes || '',
            thumbnail_link: task.thumbnail_link || '',
            client_id: task.client_id || null,
            operator_id: task.operator_id || null,
            editor_id: task.editor_id || null,
            designer_id: task.designer_id || null,
            status: task.status || 'pending',
            raw_link: task.raw_link || '',
            reference_video_link: task.reference_video_link || '',
            shoot_date: task.shoot_date || '',
            video_count: task.video_count || 1
          }));
          
          allTasks = [...allTasks, ...mappedTasks];
        }
      }

      // Process editor tasks
      if (editorTasksResponse.ok) {
        const contentType = editorTasksResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await editorTasksResponse.json();
          let editorTasksArray = [];
          
          if (Array.isArray(data)) {
            editorTasksArray = data;
          } else if (data && typeof data === 'object') {
            if (data.data && Array.isArray(data.data)) {
              editorTasksArray = data.data;
            } else if (data.success !== false) {
              editorTasksArray = [data];
            }
          }
          
          console.log('📊 Editor tasks fetched:', editorTasksArray.length);
          
          const mappedEditorTasks = editorTasksArray.map(task => ({
            ...task,
            source: 'editor-tasks',
            id: `ET-${task.id}`,
            original_id: task.id,
            toggle_status: task.toggle_status || 'not proved',
            client_feedback_notes: task.client_feedback_notes || task.client_feedback || '',
            client_status: task.client_status || 'pending',
            editor_status: task.status || 'pending',
            designer_status: task.designer_status || 'pending',
            video_name: task.video_name || task.task_name || 'Unnamed Task',
            client_name: task.client_name || 'Unknown Client',
            editor_name: task.editor_name || task.assigned_editor || task.created_by_name || 'Unassigned',
            designer_name: task.designer_name || task.assigned_designer || 'Unassigned',
            operator_name: task.operator_name || 'N/A',
            video_type: task.video_type || 'Not Specified',
            editor_link: task.editor_link || task.edited_video_link || '',
            thumbnail_link: task.thumbnail_link || '',
            notes: task.notes || '',
            client_id: task.client_id || null,
            editor_id: task.editor_id || task.created_by || task.assigned_by || null,
            designer_id: task.designer_id || null,
            operator_id: null,
            created_by: task.created_by || null,
            assigned_by: task.assigned_by || null,
            status: task.status || 'pending',
            raw_link: task.raw_link || '',
            shoot_date: task.shoot_date || task.created_at || '',
            video_count: task.video_count || 1
          }));
          
          allTasks = [...allTasks, ...mappedEditorTasks];
        }
      }

      // Process designer tasks
      if (designerTasksResponse.ok) {
        const contentType = designerTasksResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await designerTasksResponse.json();
          let designerTasksArray = [];
          
          if (Array.isArray(data)) {
            designerTasksArray = data;
          } else if (data && typeof data === 'object') {
            if (data.data && Array.isArray(data.data)) {
              designerTasksArray = data.data;
            } else if (data.success !== false) {
              designerTasksArray = [data];
            }
          }
          
          console.log('📊 Designer tasks fetched:', designerTasksArray.length);
          
          const mappedDesignerTasks = designerTasksArray.map(task => ({
            ...task,
            source: 'designer-tasks',
            id: `DT-${task.id}`,
            original_id: task.id,
            toggle_status: task.toggle_status || 'not proved',
            client_feedback_notes: task.client_feedback_notes || task.client_feedback || '',
            client_status: task.client_status || 'pending',
            editor_status: task.editor_status || 'pending',
            designer_status: task.status || 'pending',
            video_name: task.video_name || task.task_name || task.description || 'Unnamed Task',
            client_name: task.client_name || 'Unknown Client',
            editor_name: task.editor_name || 'Unassigned',
            designer_name: task.designer_name || task.assigned_designer || 'Unassigned',
            operator_name: task.operator_name || 'N/A',
            video_type: task.video_type || 'Design Task',
            thumbnail_link: task.thumbnail_link || task.thumbnail_url || task.submitted_url || '',
            notes: task.notes || task.description || task.designer_notes || '',
            client_id: task.client_id || null,
            editor_id: task.editor_id || null,
            designer_id: task.designer_id || null,
            operator_id: null,
            assigned_by: task.assigned_by || null,
            status: task.status || 'pending',
            raw_link: task.raw_link || '',
            shoot_date: task.shoot_date || task.created_at || '',
            video_count: task.video_count || 1,
            completed_at: task.completed_at || null,
            submitted_at: task.submitted_at || null
          }));
          
          allTasks = [...allTasks, ...mappedDesignerTasks];
        }
      }
      
      console.log(`📊 Fallback loaded ${allTasks.length} tasks`);
      setTasks(allTasks);
      
    } catch (error) {
      console.error('❌ Fallback also failed:', error);
      setTasks([]);
    }
  };

  // Fetch users and update task IDs after users are loaded
  const fetchUsers = async (userType) => {
    try {
      setLoadingUsers(true);
      
      const typeMapping = {
        'client': 'clients',
        'editor': 'editors',
        'designer': 'designers',
        'operator': 'operators'
      };
      
      const apiType = typeMapping[userType];
      
      const response = await fetch(`${API_BASE}/get_users.php?type=${apiType}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        const normalizedUsers = result.data
          .map(user => ({
            ...user,
            name: getUserName(user),
            status: user.status || 'Active'
          }))
          .filter(user => user.name);

        // Update the appropriate user list
        switch (userType) {
          case 'client':
            setClients(normalizedUsers);
            break;
          case 'editor':
            setEditors(normalizedUsers);
            break;
          case 'designer':
            setDesigners(normalizedUsers);
            break;
          case 'operator':
            setOperators(normalizedUsers);
            break;
          default:
            break;
        }
        
        // Update task IDs based on user names
        updateTaskIds(userType, normalizedUsers);
        
      } else {
        throw new Error(result.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setMessage('❌ Error loading users: ' + errorMessage);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Update task IDs based on user names
  const updateTaskIds = (userType, users) => {
    setTasks(prevTasks => {
      return prevTasks.map(task => {
        const updatedTask = { ...task };
        
        if (userType === 'client' && !updatedTask.client_id && updatedTask.client_name && updatedTask.client_name !== 'Unknown Client') {
          const taskClientName = normalizeName(updatedTask.client_name);
          const client = users.find(u => normalizeName(u.name) === taskClientName);
          if (client) {
            updatedTask.client_id = client.id;
          }
        }

        if (userType === 'editor' && !updatedTask.editor_id && updatedTask.editor_name && updatedTask.editor_name !== 'Unassigned' && updatedTask.editor_name !== 'N/A') {
          const taskEditorName = normalizeName(updatedTask.editor_name);
          const editor = users.find(u => normalizeName(u.name) === taskEditorName);
          if (editor) {
            updatedTask.editor_id = editor.id;
          }
        }

        if (userType === 'designer' && !updatedTask.designer_id && updatedTask.designer_name && updatedTask.designer_name !== 'Unassigned' && updatedTask.designer_name !== 'N/A') {
          const taskDesignerName = normalizeName(updatedTask.designer_name);
          const designer = users.find(u => normalizeName(u.name) === taskDesignerName);
          if (designer) {
            updatedTask.designer_id = designer.id;
          }
        }

        if (userType === 'operator' && !updatedTask.operator_id && updatedTask.operator_name && updatedTask.operator_name !== 'Unassigned' && updatedTask.operator_name !== 'N/A') {
          const taskOperatorName = normalizeName(updatedTask.operator_name);
          const operator = users.find(u => normalizeName(u.name) === taskOperatorName);
          if (operator) {
            updatedTask.operator_id = operator.id;
          }
        }
        
        return updatedTask;
      });
    });
  };

  const filterTasksByUser = () => {
    if (!selectedUserId) {
      setFilteredTasks([]);
      setSelectedUserDetails(null);
      return;
    }

    let filtered = [];
    let userDetails = null;

    console.log('🔍 Filtering tasks for:', selectedUserType, 'ID:', selectedUserId);
    console.log('📊 Total tasks available:', tasks.length);

    switch (selectedUserType) {
      case 'client':
        userDetails = clients.find(c => String(c.id) === String(selectedUserId));
        const selectedClientName = normalizeName(userDetails?.name);

        filtered = tasks.filter(task => {
          const matchesById = task.client_id !== null && task.client_id !== undefined && String(task.client_id) === String(selectedUserId);
          const matchesByName = selectedClientName && normalizeName(task.client_name) === selectedClientName;
          const matches = matchesById || matchesByName;

          if (matches) {
            console.log('✅ Client match:', task.video_name, 'from', task.source);
          }
          return matches;
        });
        break;
        
      case 'editor':
        // Filter tasks where editor_id matches OR assigned_by/created_by matches (for editor_tasks)
        filtered = tasks.filter(task => {
          const isAssignedEditor = String(task.editor_id) === String(selectedUserId);
          const isCreatedBy = String(task.created_by) === String(selectedUserId);
          const isAssignedBy = String(task.assigned_by) === String(selectedUserId);
          const matches = isAssignedEditor || isCreatedBy || isAssignedBy;
          
          if (matches) {
            console.log('✅ Editor match:', task.video_name, 'from', task.source);
          }
          return matches;
        });
        userDetails = editors.find(e => String(e.id) === String(selectedUserId));
        break;
        
      case 'designer':
        // Filter tasks where designer_id matches OR designer_name matches
        userDetails = designers.find(d => String(d.id) === String(selectedUserId));
        filtered = tasks.filter(task => {
          const matchesById = String(task.designer_id) === String(selectedUserId);
          const matchesByName = userDetails?.name && task.designer_name === userDetails.name;
          const matches = matchesById || matchesByName;
          if (matches) {
            console.log('✅ Designer match:', task.video_name, 'from', task.source);
          }
          return matches;
        });
        break;
        
      case 'operator':
        // Filter tasks where operator_id matches
        filtered = tasks.filter(task => {
          const matches = String(task.operator_id) === String(selectedUserId);
          if (matches) {
            console.log('✅ Operator match:', task.video_name, 'from', task.source);
          }
          return matches;
        });
        userDetails = operators.find(o => String(o.id) === String(selectedUserId));
        break;
        
      default:
        break;
    }

    console.log('📋 Filtered tasks count:', filtered.length);
    setFilteredTasks(filtered);
    setSelectedUserDetails(userDetails);
  };

  const getUsersList = () => {
    switch (selectedUserType) {
      case 'client':
        return clients;
      case 'editor':
        return editors;
      case 'designer':
        return designers;
      case 'operator':
        return operators;
      default:
        return [];
    }
  };

  const getUserTypeLabel = () => {
    switch (selectedUserType) {
      case 'client':
        return 'Client';
      case 'editor':
        return 'Editor';
      case 'designer':
        return 'Designer';
      case 'operator':
        return 'Operator';
      default:
        return 'User';
    }
  };

  const getUserTypeIcon = () => {
    switch (selectedUserType) {
      case 'client':
        return 'fa-user-tie';
      case 'editor':
        return 'fa-video';
      case 'designer':
        return 'fa-palette';
      case 'operator':
        return 'fa-user-cog';
      default:
        return 'fa-user';
    }
  };

  const getTaskStats = () => {
    const total = filteredTasks.length;
    const completed = filteredTasks.filter(t => t.status === 'completed').length;
    const pending = filteredTasks.filter(t => t.status === 'pending').length;
    const inProgress = filteredTasks.filter(t => t.status === 'in_progress').length;
    
    return { total, completed, pending, inProgress };
  };

  const safeFormatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return colors.success;
      case 'pending':
        return colors.warning;
      case 'in_progress':
      case 'in progress':
        return colors.info;
      case 'rejected':
        return colors.danger;
      default:
        return colors.warning;
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return 'fa-check-circle';
      case 'pending':
        return 'fa-clock';
      case 'in_progress':
      case 'in progress':
        return 'fa-spinner';
      case 'rejected':
        return 'fa-times-circle';
      default:
        return 'fa-clock';
    }
  };

  const getUserTypeStatusLabel = () => {
    switch (selectedUserType) {
      case 'client':
        return 'Client Status';
      case 'editor':
        return 'Editor Status';
      case 'designer':
        return 'Designer Status';
      case 'operator':
        return 'Task Status';
      default:
        return 'Status';
    }
  };

  const getUserTypeStatus = (task) => {
    switch (selectedUserType) {
      case 'client':
        return task.client_status || 'pending';
      case 'editor':
        return task.editor_status || 'pending';
      case 'designer':
        return task.designer_status || 'pending';
      case 'operator':
        return task.status || 'pending';
      default:
        return task.status || 'pending';
    }
  };

  const handleTextClick = (id, type, text) => {
    setHoveredText({ id, type, text });
  };

  const handleCloseTooltip = () => {
    setHoveredText({ id: null, type: '', text: '' });
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'tasks':
        return { label: 'Main Tasks', color: colors.primary };
      case 'editor-tasks':
      case 'editor_tasks':
        return { label: 'Editor Tasks', color: colors.info };
      case 'designer-tasks':
      case 'designer_tasks':
        return { label: 'Designer Tasks', color: colors.secondary };
      default:
        return { label: 'Unknown', color: colors.warning };
    }
  };

  const downloadCSV = () => {
    if (filteredTasks.length === 0) {
      setMessage('❌ No tasks to download');
      return;
    }

    let csvContent = '';
    let headers = [];
    let rows = [];

    // Define headers based on user type
    if (selectedUserType === 'editor') {
      headers = ['Source', 'ID', 'Video Name', 'Client Name', 'Status', 'Editor Status', 'Raw Link', 'Edited Link', 'Video Type', 'Shoot Date'];
      rows = filteredTasks.map(task => [
        getSourceBadge(task.source).label,
        task.original_id || task.id,
        task.video_name,
        task.client_name,
        task.status,
        task.editor_status,
        task.raw_link || 'N/A',
        task.editor_link || 'N/A',
        task.video_type,
        safeFormatDate(task.shoot_date)
      ]);
    } else if (selectedUserType === 'designer') {
      headers = ['Source', 'ID', 'Video Name', 'Client Name', 'Status', 'Designer Status', 'Edited Link', 'Thumbnail Link', 'Raw Link', 'Created Date'];
      rows = filteredTasks.map(task => [
        getSourceBadge(task.source).label,
        task.original_id || task.id,
        task.video_name,
        task.client_name,
        task.status,
        task.designer_status,
        task.editor_link || 'N/A',
        task.thumbnail_link || 'N/A',
        task.raw_link || 'N/A',
        safeFormatDate(task.shoot_date)
      ]);
    } else if (selectedUserType === 'client') {
      headers = ['Source', 'ID', 'Video Name', 'Client Name', 'Status', 'Editor Name', 'Designer Name', 'Raw Link', 'Edited Link', 'Thumbnail Link', 'Client Status', 'Client Notes'];
      rows = filteredTasks.map(task => [
        getSourceBadge(task.source).label,
        task.original_id || task.id,
        task.video_name,
        task.client_name,
        task.status,
        task.editor_name,
        task.designer_name,
        task.raw_link || 'N/A',
        task.editor_link || 'N/A',
        task.thumbnail_link || 'N/A',
        task.client_status,
        task.client_feedback_notes || 'No notes'
      ]);
    } else if (selectedUserType === 'operator') {
      headers = ['Source', 'ID', 'Video Name', 'Client Name', 'Status', 'Editor', 'Designer', 'Video Type', 'Shoot Date', 'Video Count'];
      rows = filteredTasks.map(task => [
        getSourceBadge(task.source).label,
        task.original_id || task.id,
        task.video_name,
        task.client_name,
        task.status,
        task.editor_name,
        task.designer_name,
        task.video_type,
        safeFormatDate(task.shoot_date),
        task.video_count
      ]);
    }

    // Escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Build CSV content
    csvContent = headers.map(escapeCSV).join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(escapeCSV).join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = `${selectedUserType}_tasks_${selectedUserDetails?.name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setMessage('✅ CSV downloaded successfully');
    setTimeout(() => setMessage(''), 3000);
  };

  // THE RETURN JSX REMAINS EXACTLY THE SAME AS YOUR ORIGINAL CODE
  return (
    <Layout>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <h1 style={styles.pageTitle}>
                <i className="fas fa-users-cog me-2"></i>
                Tasks by Team Members
              </h1>
              <p style={styles.pageSubtitle}>
                View all tasks and details for specific clients, editors, designers, or operators
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={downloadCSV}
                style={{
                  ...styles.refreshBtn,
                  background: filteredTasks.length > 0 ? `linear-gradient(135deg, ${colors.success} 0%, ${colors.accent} 100%)` : styles.refreshBtn.background,
                  border: filteredTasks.length > 0 ? 'none' : styles.refreshBtn.border
                }}
                disabled={filteredTasks.length === 0}
              >
                <i className="fas fa-download"></i>
                Download CSV
              </button>
              <button 
                onClick={fetchTasks}
                style={styles.refreshBtn}
                disabled={loading}
              >
                <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div style={message.includes('❌') ? styles.messageAlert : styles.successAlert}>
            <i className={`fas ${message.includes('❌') ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
            {message}
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          {/* Filter Card */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-filter me-2"></i>
                Select User to View Tasks
              </h3>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>User Type</label>
                  <select
                    style={styles.dropdownSelect}
                    value={selectedUserType}
                    onChange={(e) => {
                      setSelectedUserType(e.target.value);
                      setSelectedUserId('');
                      setSelectedUserDetails(null);
                      setFilteredTasks([]);
                    }}
                  >
                    <option value="client">Client</option>
                    <option value="editor">Editor</option>
                    <option value="designer">Designer</option>
                    <option value="operator">Operator</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Select {getUserTypeLabel()}</label>
                  <select
                    style={styles.dropdownSelect}
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">-- Select {getUserTypeLabel()} --</option>
                    {getUsersList().map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => {
                    setSelectedUserId('');
                    setSelectedUserDetails(null);
                    setFilteredTasks([]);
                  }}
                  style={styles.submitBtn}
                >
                  <i className="fas fa-times me-2"></i>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>

          {/* User Details Card */}
          {selectedUserDetails && (
            <div style={styles.userDetailsCard}>
              <h3 style={styles.cardTitle}>
                <i className={`fas ${getUserTypeIcon()} me-2`}></i>
                {getUserTypeLabel()} Details
              </h3>
              
              <div style={styles.userDetailsGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Name</span>
                  <span style={styles.detailValue}>{selectedUserDetails.name}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{selectedUserDetails.email}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={{
                    ...styles.statusBadge,
                    background: selectedUserDetails.status === 'Active' ? colors.success : colors.danger
                  }}>
                    {selectedUserDetails.status || 'Active'}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>User ID</span>
                  <span style={styles.detailValue}>#{selectedUserDetails.id}</span>
                </div>
              </div>

              {/* Task Statistics */}
              <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: colors.primary}}>
                    <i className="fas fa-tasks"></i>
                  </div>
                  <div style={styles.statContent}>
                    <div style={styles.statLabel}>Total Tasks</div>
                    <div style={styles.statValue}>{getTaskStats().total}</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: colors.success}}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div style={styles.statContent}>
                    <div style={styles.statLabel}>Completed</div>
                    <div style={styles.statValue}>{getTaskStats().completed}</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: colors.warning}}>
                    <i className="fas fa-clock"></i>
                  </div>
                  <div style={styles.statContent}>
                    <div style={styles.statLabel}>Pending</div>
                    <div style={styles.statValue}>{getTaskStats().pending}</div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: colors.info}}>
                    <i className="fas fa-spinner"></i>
                  </div>
                  <div style={styles.statContent}>
                    <div style={styles.statLabel}>In Progress</div>
                    <div style={styles.statValue}>{getTaskStats().inProgress}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tasks Table */}
          {selectedUserId && (
            <div style={styles.formCard}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-list me-2"></i>
                  Tasks for {selectedUserDetails?.name}
                  <span style={styles.statsBadge}>
                    {filteredTasks.length} Task{filteredTasks.length !== 1 ? 's' : ''}
                  </span>
                </h3>
              </div>

              {loading ? (
                <div style={styles.loadingContainer}>
                  <div style={styles.spinner}></div>
                  <h3>Loading Tasks...</h3>
                  <p>Please wait while we fetch the data</p>
                </div>
              ) : filteredTasks.length === 0 ? (
                <div style={styles.emptyState}>
                  <i className="fas fa-inbox" style={{fontSize: '4rem', marginBottom: '20px', opacity: 0.5}}></i>
                  <h3>No Tasks Found</h3>
                  <p>No tasks assigned to this {getUserTypeLabel().toLowerCase()} yet.</p>
                </div>
              ) : (
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.tableHeader}>Source</th>
                        <th style={styles.tableHeader}>ID</th>
                        <th style={styles.tableHeader}>Video Name</th>
                        <th style={styles.tableHeader}>Client Name</th>
                        <th style={styles.tableHeader}>Status</th>
                        
                        {/* Editor View */}
                        {selectedUserType === 'editor' && (
                          <>
                            <th style={styles.tableHeader}>Editor Status</th>
                            <th style={styles.tableHeader}>Raw Link</th>
                            <th style={styles.tableHeader}>Edited Link</th>
                            <th style={styles.tableHeader}>Video Type</th>
                            <th style={styles.tableHeader}>Shoot Date</th>
                          </>
                        )}
                        
                        {/* Designer View */}
                        {selectedUserType === 'designer' && (
                          <>
                            <th style={styles.tableHeader}>Designer Status</th>
                            <th style={styles.tableHeader}>Edited Link</th>
                            <th style={styles.tableHeader}>Thumbnail Link</th>
                            <th style={styles.tableHeader}>Raw Link</th>
                            <th style={styles.tableHeader}>Created Date</th>
                          </>
                        )}
                        
                        {/* Client View */}
                        {selectedUserType === 'client' && (
                          <>
                            <th style={styles.tableHeader}>Editor Name</th>
                            <th style={styles.tableHeader}>Designer Name</th>
                            <th style={styles.tableHeader}>Raw Link</th>
                            <th style={styles.tableHeader}>Edited Link</th>
                            <th style={styles.tableHeader}>Thumbnail Link</th>
                            <th style={styles.tableHeader}>Client Status</th>
                            <th style={styles.tableHeader}>Client Notes</th>
                          </>
                        )}
                        
                        {/* Operator View */}
                        {selectedUserType === 'operator' && (
                          <>
                            <th style={styles.tableHeader}>Editor</th>
                            <th style={styles.tableHeader}>Designer</th>
                            <th style={styles.tableHeader}>Video Type</th>
                            <th style={styles.tableHeader}>Shoot Date</th>
                            <th style={styles.tableHeader}>Video Count</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.map(task => {
                        const sourceBadge = getSourceBadge(task.source);
                        const userStatus = getUserTypeStatus(task);
                        
                        return (
                          <tr key={task.id} style={styles.tableRow}>
                            <td style={styles.tableCell}>
                              <span style={{
                                ...styles.statusBadge,
                                background: sourceBadge.color,
                                fontSize: '0.7rem'
                              }}>
                                {sourceBadge.label}
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              <span style={styles.tableText}>#{task.original_id || task.id}</span>
                            </td>
                            <td style={styles.tableCell}>
                              <span 
                                style={styles.textCell}
                                onClick={() => handleTextClick(task.id, 'video_name', task.video_name)}
                                title="Click to view full text"
                              >
                                {task.video_name}
                              </span>
                            </td>
                            <td style={styles.tableCell}>
                              <span style={styles.tableText}>{task.client_name}</span>
                            </td>
                            <td style={styles.tableCell}>
                              <span style={{
                                ...styles.statusBadge,
                                background: getStatusColor(task.status)
                              }}>
                                <i className={`fas ${getStatusIcon(task.status)}`}></i>
                                {task.status}
                              </span>
                            </td>
                            
                            {/* Editor View Cells */}
                            {selectedUserType === 'editor' && (
                              <>
                                <td style={styles.tableCell}>
                                  <span style={{
                                    ...styles.statusBadge,
                                    background: getStatusColor(task.editor_status)
                                  }}>
                                    <i className={`fas ${getStatusIcon(task.editor_status)}`}></i>
                                    {task.editor_status}
                                  </span>
                                </td>
                                <td style={styles.tableCell}>
                                  {task.raw_link ? (
                                    <a 
                                      href={task.raw_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-link"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  {task.editor_link ? (
                                    <a 
                                      href={task.editor_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-video"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.video_type}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{safeFormatDate(task.shoot_date)}</span>
                                </td>
                              </>
                            )}
                            
                            {/* Designer View Cells */}
                            {selectedUserType === 'designer' && (
                              <>
                                <td style={styles.tableCell}>
                                  <span style={{
                                    ...styles.statusBadge,
                                    background: getStatusColor(task.designer_status)
                                  }}>
                                    <i className={`fas ${getStatusIcon(task.designer_status)}`}></i>
                                    {task.designer_status}
                                  </span>
                                </td>
                                <td style={styles.tableCell}>
                                  {task.editor_link ? (
                                    <a 
                                      href={task.editor_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-video"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  {task.thumbnail_link ? (
                                    <a 
                                      href={task.thumbnail_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-image"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  {task.raw_link ? (
                                    <a 
                                      href={task.raw_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-link"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{safeFormatDate(task.shoot_date)}</span>
                                </td>
                              </>
                            )}
                            
                            {/* Client View Cells */}
                            {selectedUserType === 'client' && (
                              <>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.editor_name}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.designer_name}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  {task.raw_link ? (
                                    <a 
                                      href={task.raw_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-link"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  {task.editor_link ? (
                                    <a 
                                      href={task.editor_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-video"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  {task.thumbnail_link ? (
                                    <a 
                                      href={task.thumbnail_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{...styles.linkButton, textDecoration: 'none'}}
                                    >
                                      <i className="fas fa-image"></i> View
                                    </a>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>N/A</span>
                                  )}
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={{
                                    ...styles.statusBadge,
                                    background: getStatusColor(task.client_status)
                                  }}>
                                    <i className={`fas ${getStatusIcon(task.client_status)}`}></i>
                                    {task.client_status}
                                  </span>
                                </td>
                                <td style={styles.tableCell}>
                                  {task.client_feedback_notes ? (
                                    <span 
                                      style={styles.textCell}
                                      onClick={() => handleTextClick(task.id, 'client_notes', task.client_feedback_notes)}
                                      title="Click to view full notes"
                                    >
                                      {task.client_feedback_notes}
                                    </span>
                                  ) : (
                                    <span style={{color: colors.textLight, fontSize: '0.75rem'}}>No notes</span>
                                  )}
                                </td>
                              </>
                            )}
                            
                            {/* Operator View Cells */}
                            {selectedUserType === 'operator' && (
                              <>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.editor_name}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.designer_name}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.video_type}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{safeFormatDate(task.shoot_date)}</span>
                                </td>
                                <td style={styles.tableCell}>
                                  <span style={styles.tableText}>{task.video_count}</span>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Text Tooltip */}
        {hoveredText.id && (
          <div style={styles.tooltip}>
            <div style={styles.tooltipHeader}>
              <strong>{hoveredText.type.replace('_', ' ').toUpperCase()}</strong>
              <button onClick={handleCloseTooltip} style={styles.closeButton}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div>{hoveredText.text}</div>
          </div>
        )}

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </Layout>
  );
}

export default TasksByUser;